import React from 'react';
import { RotateCcw, Search } from 'lucide-react';
import { useUsers, useProjects } from '../../hooks/useApi';
import { Button, Card } from '../../components/ui';

const STATUS_OPTIONS = [
    { value: 'todo', label: 'To do' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'blocked', label: 'Blocked' },
    { value: 'done', label: 'Done' },
    { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
];

/** Toggle chip used for the multi-select status and priority rows. */
const FilterChip = ({ selected, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={`h-[26px] px-2.5 rounded-sm text-label font-medium border transition-colors duration-150 ${selected
            ? 'bg-primary-50 border-primary-200 text-primary-600'
            : 'bg-white border-[var(--border-color)] text-ink-600 hover:border-[var(--border-strong)] hover:text-ink-900'
            }`}
    >
        {children}
    </button>
);

const Filters = ({ filters, onChange, onReset, onApply }) => {
    const { data: userResult } = useUsers();
    const { data: projectResult } = useProjects();
    const users = userResult?.data || [];
    const projects = projectResult?.data || [];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        onChange({ ...filters, [name]: type === 'checkbox' ? checked : value });
    };

    const toggleMultiSelect = (field, value) => {
        const current = filters[field] ? filters[field].split(',').filter(Boolean) : [];
        const next = current.includes(value)
            ? current.filter((v) => v !== value)
            : [...current, value];
        onChange({ ...filters, [field]: next.join(',') });
    };

    return (
        <Card className="no-print">
            <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
                    <div className="xl:col-span-2">
                        <label htmlFor="rp-search" className="form-label">
                            Search
                        </label>
                        <div className="relative">
                            <Search
                                size={14}
                                aria-hidden="true"
                                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none"
                            />
                            <input
                                id="rp-search"
                                type="search"
                                name="search"
                                value={filters.search || ''}
                                onChange={handleChange}
                                placeholder="Tasks, projects or people…"
                                className="input input-search"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="rp-start" className="form-label">
                            Start date
                        </label>
                        <input
                            id="rp-start"
                            type="date"
                            name="startDate"
                            value={filters.startDate || ''}
                            onChange={handleChange}
                            className="input"
                        />
                    </div>

                    <div>
                        <label htmlFor="rp-end" className="form-label">
                            End date
                        </label>
                        <input
                            id="rp-end"
                            type="date"
                            name="endDate"
                            value={filters.endDate || ''}
                            onChange={handleChange}
                            className="input"
                        />
                    </div>

                    <div>
                        <label htmlFor="rp-project" className="form-label">
                            Project
                        </label>
                        <select
                            id="rp-project"
                            name="projectId"
                            value={filters.projectId || ''}
                            onChange={handleChange}
                            className="input"
                        >
                            <option value="">All projects</option>
                            {projects?.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.project_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label htmlFor="rp-assignee" className="form-label">
                            Assignee
                        </label>
                        <select
                            id="rp-assignee"
                            name="assigneeId"
                            value={filters.assigneeId || ''}
                            onChange={handleChange}
                            className="input"
                        >
                            <option value="">All users</option>
                            {users?.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.first_name} {u.last_name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-label text-ink-500 mr-1">Status</span>
                        {STATUS_OPTIONS.map((opt) => (
                            <FilterChip
                                key={opt.value}
                                selected={(filters.status || '').split(',').includes(opt.value)}
                                onClick={() => toggleMultiSelect('status', opt.value)}
                            >
                                {opt.label}
                            </FilterChip>
                        ))}
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-label text-ink-500 mr-1">Priority</span>
                        {PRIORITY_OPTIONS.map((opt) => (
                            <FilterChip
                                key={opt.value}
                                selected={(filters.priority || '').split(',').includes(opt.value)}
                                onClick={() => toggleMultiSelect('priority', opt.value)}
                            >
                                {opt.label}
                            </FilterChip>
                        ))}
                    </div>

                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            name="isOverdue"
                            checked={filters.isOverdue === true || filters.isOverdue === 'true'}
                            onChange={(e) => onChange({ ...filters, isOverdue: e.target.checked })}
                            className="w-3.5 h-3.5 accent-[var(--danger)]"
                        />
                        <span className="text-base text-ink-700">Overdue only</span>
                    </label>
                </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-[var(--divider-color)] bg-[var(--bg-subtle)] rounded-b-lg">
                <Button variant="ghost" icon={RotateCcw} onClick={onReset}>
                    Reset
                </Button>
                <Button variant="primary" icon={Search} onClick={onApply}>
                    Apply filters
                </Button>
            </div>
        </Card>
    );
};

export default Filters;
